import { useEffect, useState, useMemo } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import BlogHeroSection from './components/BlogHeroSection';
import BlogCategoryFilter from './components/BlogCategoryFilter';
import BlogArticleCard from './components/BlogArticleCard';
import { blogArticles, blogCategories } from '@/mocks/blogData';
import Seo from '@/components/seo/Seo';

export default function BlogListPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filteredArticles = useMemo(() => {
    if (!activeCategory) return blogArticles;
    return blogArticles.filter((a) => a.categoryId === activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -60px 0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('.fade-in-section');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [activeCategory]);

  return (
    <>
      <Seo route="/blog" />

      <Header />

      <main>
        <BlogHeroSection />
      <Breadcrumb items={[{ label: t('common.nav.contact.blog') ?? '講師ブログ' }]} />
        <BlogCategoryFilter
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />

        {/* Article Grid */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <div key={article.slug} className="fade-in-section">
                    <BlogArticleCard article={article} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-[16px] text-foreground-500 tracking-[0.04em]">
                  {t('blog.list.empty')}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Blog Footer Info */}
        <section className="py-12 bg-background-100 border-t border-background-200/50">
          <div className="max-w-[800px] mx-auto px-8 text-center">
            <h2 className="font-heading text-lg text-foreground-900 tracking-[0.06em] mb-4">
              {t('blog.list.categoryTitle')}
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {blogCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className="px-4 py-1.5 text-[15px] text-foreground-600 hover:text-accent-600 tracking-[0.04em] transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  {t(`blog.cat.${cat.id}`, { defaultValue: cat.name })}
                </button>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
